import React, { useEffect, useState } from "react";
import { API_URL } from "../config";
import { formatTimestamp } from "../utils/TextUtils";

// "Sat, 24 Oct 2026"
const formatDay = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
};

const formatKes = (amount) =>
    `KES ${Number(amount).toLocaleString("en-KE", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const STATUS_NAMES = {
    PendingPayment: "Awaiting payment",
    UnApproved: "Waiting for approval",
};

// A member's membership history: sign-up, email confirmation, approval, expiries and
// every fee paid. Admins and super admins see it on any member's page (with who did
// what); a member sees their own on their profile. The API decides who may see it.
function MembershipHistory({ userId, title = "Membership history" }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!userId) return undefined;
        let cancelled = false;

        fetch(`${API_URL}/users/${userId}/membership-history`, {
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
        })
            .then(async (response) => {
                if (!response.ok) throw new Error(String(response.status));
                return response.json();
            })
            .then((result) => {
                if (!cancelled) setData(result);
            })
            .catch(() => {
                if (!cancelled) setError("The membership history could not be loaded.");
            });

        return () => {
            cancelled = true;
        };
    }, [userId]);

    const member = data?.member;
    const events = data?.events || [];
    const showWho = events.some((event) => event.performed_by);

    return (
        <div className="box cf-history">
            <p className="title is-5">{title}</p>

            {error && <p className="cf-muted">{error}</p>}
            {!data && !error && <p className="cf-muted">Loading...</p>}

            {member && (
                <dl className="cf-history-facts">
                    <div><dt>Signed up</dt><dd>{formatDay(member.signed_up_at) || "Not known"}</dd></div>
                    <div><dt>Email confirmed</dt><dd>{formatDay(member.email_confirmed_at) || "Not yet"}</dd></div>
                    <div><dt>Last renewed</dt><dd>{formatDay(member.last_renewed_at) || "Never"}</dd></div>
                    <div>
                        <dt>Total paid</dt>
                        <dd>{data.payments_count > 0 ? `${formatKes(data.total_paid)} (${data.payments_count} payment${data.payments_count === 1 ? "" : "s"})` : "Nothing yet"}</dd>
                    </div>
                    <div><dt>Status now</dt><dd>{STATUS_NAMES[member.status] || member.status}</dd></div>
                </dl>
            )}

            {data && events.length === 0 && <p className="cf-muted">Nothing has been recorded yet.</p>}

            {events.length > 0 && (
                <div className="table-container">
                    <table className="table is-fullwidth is-striped">
                        <thead>
                            <tr>
                                <th>Date and time</th>
                                <th>What happened</th>
                                <th>Amount paid</th>
                                <th>M-Pesa code</th>
                                {showWho && <th>By</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {events.map((event) => (
                                <tr key={event.id}>
                                    <td className="cf-nowrap">{event.occurred_at ? formatTimestamp(event.occurred_at) : "-"}</td>
                                    <td>
                                        {event.label}
                                        {event.note && <span className="cf-muted is-size-7"><br />{event.note}</span>}
                                        {event.from_earlier_records && (
                                            <span className="cf-muted is-size-7"><br />From earlier records</span>
                                        )}
                                    </td>
                                    <td className="cf-nowrap">{event.amount != null ? formatKes(event.amount) : "-"}</td>
                                    <td>{event.payment_code || "-"}</td>
                                    {showWho && <td>{event.performed_by || "-"}</td>}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default MembershipHistory;
